/**
 * DBE Keeper 디바이스 등록 / 자동 잠금해제 오케스트레이션 (Issue #1291, 설계 §3.3 + §1.2.1 v1.5).
 *
 * wrap 대상 = UMK (v1.5). eccCrypto wrap/unwrap 은 값-agnostic 이라 UMK 문자열을 그대로 wrap/unwrap.
 * WebAuthn 신원 검증(startAuthentication) + self-ECDH 디바이스 키(IndexedDB, extractable=false) 조합.
 *
 * staleness: changePW/recovery 시 서버가 디바이스를 일괄 revoke(설계 §1.2.1) → 다음 unlock 에서
 * SAFEBOX_841(device not found) → 호출 측(LoginMasterPW)이 clearDeviceKeys + MPW 폴백.
 */
import { startAuthentication } from '@simplewebauthn/browser'
import store from 'store'

import { decodeJwtPayload } from 'services/jwt/decode' // 의존성 zero 모듈 (B′)
// #1509: 등록 시 서버가 wrap 대상 UMK 의 신선도를 검증하도록 old_hmk 동봉.
// 로그인/recovery 와 동일한 forge hashString 을 재사용해 서버 user.hmk 와 바이트 동일 해시 보장
// (crypto.subtle+TextEncoder 로 대체 금지 — 비-ASCII 비밀번호에서 인코딩 불일치).
import { hashString } from 'pUtils'

import {
  deleteCurrentDevice,
  fetchDeviceRegisterOptions,
  fetchDeviceUnlockOptions,
  postDeviceRegister,
  postDeviceUnlock,
} from 'api/authDevice'

import {
  generateClientEcdhKeypair,
  selfDeriveWrapKey,
  unwrapVaultKey,
  wrapVaultKey,
} from './eccCrypto'
import { idbClearUser, idbGet, idbHasDevice, idbPut } from './idb'
import { PasskeyApiError } from './index'
import { parseDeviceLabel } from './userAgent'

const FIELD_PRIV = 'client-ecdh-priv'
const FIELD_PUB = 'client-ecdh-pub'
const FIELD_DEVICE_ID = 'device-id'

// 로그아웃(landing.removeAllAppUserKeys)이 app.user.* 전체를 지우므로 매핑은 app.dbe.* 에 둔다.
const SCOPE_MAP_PREFIX = 'app.dbe.scope.'

/**
 * IndexedDB user 스코프 식별자. vault open 전(LoginMasterPW)에도 사용 가능해야 하므로,
 * 로그인 시 저장된 authUuid(common-auth user_uuid) 우선, 없으면 email
 * (LoginMasterPW 의 fingerprint 소스와 동일 — index.js:162).
 *
 * authUuid 는 MPW 입력(RSAEncDec.login) 후에만 복원되는데 로그아웃이 이를 지우므로,
 * 재로그인 직후(MPW 전)에는 1차 인증 JWT 가 운반하는 user_uuid 를 사용한다 (B′,
 * dbe_scope_jwt_fallback.md). JWT 의 user_uuid 는 authUuid 와 동일 원천·동일 값
 * (common-auth users.user_uuid → User.common_auth_user_uuid → 로그인 응답, user.py:342)
 * 이라 기존 등록 스코프와 즉시 일치한다. 서명 검증은 불요 — 로컬 IndexedDB 조회 키일 뿐이고
 * 서버 unlock 은 JWT 서명 + user-scoped 검증을 별도 수행.
 * 매핑(#1302)·email 은 후순위 방어 (과도기 uuid 미마이그레이션 유저는 등록도 email 스코프라 정합).
 */
export function getDeviceScopeId() {
  const authUuid = store.get('app.user.authUuid')
  const email = store.get('app.user.email')
  if (authUuid) {
    if (email) {
      // store.set 은 런타임 quota 초과 시 throw (store.js 는 init 시 storage 테스트만 하고
      // write 경로엔 try-catch 없음). 매핑 저장 실패는 재로그인 후 복원만 잃을 뿐이므로
      // 로그인 크리티컬 경로(autoUnlockDevice)를 깨지 않도록 삼킨다 (Gemini 리뷰 #1298).
      try {
        store.set(SCOPE_MAP_PREFIX + email, authUuid)
      } catch (e) {
        console.warn('[DBE] scope mapping save failed:', e)
      }
    }
    return authUuid
  }
  const jwtUuid = normalizeUuid(decodeJwtPayload(store.get('app.user.token'))?.user_uuid)
  if (jwtUuid) return jwtUuid
  if (email) return store.get(SCOPE_MAP_PREFIX + email) || email
  return null
}

/**
 * JWT user_uuid 를 BE _parse_user_uuid(common/middleware.py:44-67)와 동일 규칙으로 정규화.
 * BE 는 str(UUID(raw)) = 소문자 하이픈 36자 표준형 — common-auth 가 대문자/중괄호/urn:uuid:
 * 형식으로 발급해도 authUuid(로그인 응답 경유)는 항상 표준형이므로 FE 도 미러링해야 스코프 일치.
 * 형식 불일치는 null (다음 fallback 으로).
 */
function normalizeUuid(raw) {
  if (typeof raw !== 'string') return null
  let m = raw
    .trim()
    .toLowerCase()
    .replace(/^urn:uuid:/, '')
    .replace(/^\{/, '')
    .replace(/\}$/, '')
  // BE 의 uuid.UUID() 는 하이픈 없는 32자 hex 도 수용해 표준형으로 변환 — 동일 미러 (verify 실측)
  if (/^[0-9a-f]{32}$/.test(m)) {
    m = `${m.slice(0, 8)}-${m.slice(8, 12)}-${m.slice(12, 16)}-${m.slice(16, 20)}-${m.slice(20)}`
  }
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/.test(m) ? m : null
}

function assertOk(resp) {
  if (!resp || resp.code !== 200) {
    throw new PasskeyApiError(resp?.code || 0, resp?.message)
  }
  return resp.data
}

/**
 * 현재 디바이스 등록: 본인확인 → self-ECDH 키 생성 → UMK wrap → 서버 저장 + IndexedDB.
 * 전제: vault open (umk 보유). 첫 패스키 등록 직후 호출 (설계 §4.2 C1).
 *
 * PR-2 (#1301, dbe_passkey_ux_followup.md §5.2): registrationToken(패스키 등록 응답의 1회용
 * 토큰)이 있으면 두 번째 WebAuthn assertion 을 생략한다 — 본인확인은 발급 시점의 attestation
 * 검증이 담당. 토큰 실패 시 기존 assertion 경로로 1회 fallback (840=이미 등록은 성공 취급).
 */
export async function registerCurrentDevice(umk, { registrationToken } = {}) {
  if (!umk) throw new PasskeyApiError(0, 'UMK is required for device registration')
  const userId = getDeviceScopeId()
  if (!userId) throw new PasskeyApiError(0, 'user scope id not found')

  if (registrationToken) {
    try {
      return await registerDeviceWithFields(umk, userId, {
        registration_token: registrationToken,
      })
    } catch (e) {
      if (e?.code === 840) {
        // 동시 등록 race 로 이미 등록됨 — 성공 취급 (불필요한 WebAuthn 프롬프트 방지, 리뷰 F1)
        return null
      }
      // 토큰 만료/소비(830) 등 → 기존 assertion 경로로 1회 fallback
      console.warn('[DBE] token register failed, fallback to assertion:', e)
    }
  }

  // 1) 기존 패스키로 본인 재확인 (challenge sign) — 세션 탈취 방어
  const { challenge_id: challengeId, options } = assertOk(await fetchDeviceRegisterOptions())
  const credentialResponse = await startAuthentication({ optionsJSON: options })
  return registerDeviceWithFields(umk, userId, {
    challenge_id: challengeId,
    credential_response: credentialResponse,
  })
}

/** 본인확인 필드(토큰 또는 challenge+assertion)를 받아 키 생성→wrap→서버 등록→IndexedDB 저장. */
async function registerDeviceWithFields(umk, userId, challengeFields) {
  // 2) self-ECDH 디바이스 키쌍 (extractable=false) + wrap_key 유도
  const keypair = await generateClientEcdhKeypair()
  const wrapKey = await selfDeriveWrapKey(keypair.privateKey, keypair.publicKey)
  const clientPubJwk = await crypto.subtle.exportKey('jwk', keypair.publicKey)

  // 3) UMK wrap (값-agnostic — UMK 문자열)
  const { wrapped_vault_key: wrappedVaultKey, iv } = await wrapVaultKey(wrapKey, umk)

  // 4) 서버 등록
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : ''
  const data = assertOk(
    await postDeviceRegister({
      ...challengeFields,
      client_pub_jwk: clientPubJwk,
      wrapped_vault_key: wrappedVaultKey,
      iv,
      old_hmk: hashString(umk),
      device_label: parseDeviceLabel(ua),
      user_agent: ua,
    }),
  )

  // 5) IndexedDB 저장 (priv/pub CryptoKey extractable=false + 서버 발급 device-id).
  // device_id 는 서버 응답에서 오므로 서버 등록을 먼저 한다. 단 로컬 저장이 실패하면 서버에만
  // 디바이스가 남는 orphan(클라이언트 키 없어 unlock 불가) 이 되므로, 서버 디바이스를 revoke 하여
  // 클라이언트↔서버 일관성을 유지한다 (Gemini 리뷰 #1).
  try {
    await idbPut(userId, FIELD_PRIV, keypair.privateKey)
    await idbPut(userId, FIELD_PUB, keypair.publicKey)
    await idbPut(userId, FIELD_DEVICE_ID, data.device_id)
  } catch (e) {
    try {
      await deleteCurrentDevice(data.device_id)
    } catch {
      // 서버 정리 실패해도 30일 stale cleanup 이 흡수 — 로컬 정리는 계속
    }
    await idbClearUser(userId).catch(() => {})
    throw e
  }
  return data
}

/**
 * 자동 잠금해제: IndexedDB 디바이스 키 → unlock challenge → assertion → wrapped UMK unwrap → UMK 반환.
 * 디바이스 미등록(IndexedDB 비어있음)이면 null 반환(MPW 입력 화면). 실패는 PasskeyApiError/DOMException throw.
 */
export async function autoUnlockDevice() {
  const userId = getDeviceScopeId()
  if (!userId) return null
  if (!(await idbHasDevice(userId))) return null

  const deviceId = await idbGet(userId, FIELD_DEVICE_ID)
  const privKey = await idbGet(userId, FIELD_PRIV)
  const pubKey = await idbGet(userId, FIELD_PUB)
  if (!deviceId || !privKey || !pubKey) return null

  // 1) unlock challenge (device_id binding) — 디바이스 폐기 시 여기서 SAFEBOX_841/843
  const { challenge_id: challengeId, options } = assertOk(await fetchDeviceUnlockOptions(deviceId))
  // 2) WebAuthn assertion
  const credentialResponse = await startAuthentication({ optionsJSON: options })
  // 3) unlock → wrapped UMK
  const { wrapped_vault_key: wrappedVaultKey, iv } = assertOk(
    await postDeviceUnlock({
      device_id: deviceId,
      challenge_id: challengeId,
      credential_response: credentialResponse,
    }),
  )
  // 4) self-ECDH unwrap → UMK 문자열
  const wrapKey = await selfDeriveWrapKey(privKey, pubKey)
  return unwrapVaultKey(wrapKey, wrappedVaultKey, iv)
}

/** Settings 토글 OFF: 서버 디바이스 해제 + IndexedDB 정리. 서버 실패해도 로컬 정리는 진행. */
export async function revokeCurrentDevice() {
  const userId = getDeviceScopeId()
  if (!userId) return
  const deviceId = await idbGet(userId, FIELD_DEVICE_ID)
  if (deviceId) {
    try {
      await deleteCurrentDevice(deviceId)
    } catch {
      // 이미 서버에서 revoke 됐을 수 있음(841) — 로컬 정리는 계속.
    }
  }
  await idbClearUser(userId)
}

/** 자동 잠금해제 실패(SAFEBOX_841/843 디바이스 폐기) 시 IndexedDB 정리. */
export async function clearDeviceKeys() {
  const userId = getDeviceScopeId()
  if (userId) await idbClearUser(userId)
}

/** 현재 디바이스 등록 여부 (Settings 토글 표시용). */
export async function hasRegisteredDevice() {
  const userId = getDeviceScopeId()
  if (!userId) return false
  return idbHasDevice(userId)
}

/**
 * 패스키 등록 직후 현 브라우저 디바이스 자동 등록 (PR-2 #1301, 설계 §5.2-(3)).
 *
 * 등록 3개 진입점(PasskeyManagement C1 / ManagePasskeysModal / PasskeyAutoFlow) 공통.
 * result.deviceRegistrationToken(completePasskeyRegistration 반환, #1329)이 있으면 프롬프트 0회, 없거나
 * 실패하면 assertion 1회로 fallback. 실패는 비차단 — Settings '이 브라우저에 생성'으로 재시도 가능.
 *
 * @returns {Promise<boolean>} 디바이스 사용 가능 상태면 true(신규 등록 성공/이미 등록),
 *   실패·umk 부재면 false — 호출부가 '자동 잠금해제 설정 실패' 안내를 띄울 수 있게 (#1319)
 */
export async function maybeRegisterCurrentDevice(umk, result) {
  if (!umk) return false
  try {
    // IndexedDB 조회 실패(시크릿 모드 등)도 비차단 — throw 시 호출부(PasskeyAutoFlow 등)의
    // 후속 흐름(clearFlow)이 막히지 않도록 try 안에서 검사 (gemini 리뷰 #1305)
    if (await hasRegisteredDevice()) return true // 이미 등록된 브라우저 — 840 예방
    await registerCurrentDevice(umk, {
      registrationToken: result?.deviceRegistrationToken,
    })
    return true
  } catch (e) {
    console.warn('[DBE] auto device register failed:', e)
    return false
  }
}
